import { useFormik } from 'formik';
import { TFunction } from 'i18next';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import * as Yup from 'yup';

import defaultImage from 'src/assets/images/default-icon.png';
import { CALCULATOR_TYPE, POSITION_SIDE, REGEX } from 'src/common/constants';
import {
    Button,
    Modal,
    ModalTitle,
    ModalContent,
    Tabs,
    Tab,
    Input,
    Select,
    SelectItem,
} from 'src/components/core';
import { Cross } from 'src/components/core/icons';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { performCalculations } from 'src/setup/redux/slices/derivativesSlice';
import { TokenObj } from 'src/types/configV2';
import {
    CalculatorQueryParamsType,
    ELPCalculationType,
    RORCalculationType,
} from 'src/types/derivatives';

interface CalculatorModalProps {
    isOpen: boolean;
    handleClose: () => void;
}

const StyledModal = styled(Modal)`
    ${({ theme: { isMobile, colors, typography } }) => `
        .modal-paper {
            width: ${isMobile ? `343px` : `607px`};
        }
        .modal-title {
            padding: 0px 16px;
            border-bottom: 1px solid ${colors.border.dark};
            & > .calculator-type {
                padding: 0px 8px;
            }
            
        }
        .modal-content {
            display: ${isMobile ? 'inline-block' : 'flex'};
            padding: 24px 16px;
            & > .col{
                flex: 1;
                
            }
            & > .col-left{
                margin-right: 9px;
            }
            & > .col-right{
                margin-left: 9px;
                padding: 24px 16px;
                background: ${colors.background.secondary.v2};
            }
            .calculations{
                display: ${isMobile ? 'flex' : 'block'};
                padding-bottom: ${isMobile ? '15px' : '16px'};
                .title{
                    font-size: ${typography.size.desktop.paragraph.p3};
                    font-weight: ${typography.weight.normal};
                    color: ${colors.text.tertiary};
                    flex: 1;
                    margin-bottom: 2px;
                }
                .value{
                    font-size: ${typography.size.desktop.paragraph.p2};
                    font-weight: ${typography.weight.normal};
                    flex: 1;
                }
            }
            .calculation-side {
                margin-bottom: 15px;
                border-radius: 4px;
                padding: 0px;
                .tab-button {
                    flex: 1;
                    justify-content: center;
                    margin: 0px !important;
                    background: ${colors.background.secondary.v3};
                    padding: 6px;
                    font-size: ${typography.size.desktop.paragraph.p3}
                    
                }
                .tab-active {
                    color: ${colors.primary};
                    background: ${colors.background.secondary.v4};
                    border-radius: 4px;
                }
            }
            .select{
                margin-bottom: 15px;
                img{
                    height: 20px;
                    width: 20px;
                    margin-right: 8px;
                }
            }
           .input-container {
             font-size: ${typography.size.desktop.paragraph.p3};
             font-weight: ${typography.weight.normal};
           }
           button {
             font-size: ${typography.size.desktop.paragraph.p2};
             font-weight: ${typography.weight.medium};
             padding: 11px;
             margin-top: 32px;
           }
        }
    `}
`;

const initialCalculatorValues = {
    symbol: '',
    calculatorType: CALCULATOR_TYPE.ROR,
    leverage: '',
    openPrice: '',
    entryQuantity: '',
    closePrice: '',
    closeQuantity: '',
    marginAdded: '',
    side: POSITION_SIDE.LONG,
};

const calculatorValidationSchema = (t: TFunction<'translation', undefined>) => {
    return Yup.object().shape({
        leverage: Yup.number().required(
            t('form_validation.field_required', { field: t('leverage') }),
        ),
        openPrice: Yup.number().required(
            t('form_validation.field_required', { field: t('open_price') }),
        ),
        entryQuantity: Yup.number().required(
            t('form_validation.field_required', { field: t('entry_quantity') }),
        ),
        closePrice: Yup.number().when('calculatorType', {
            is: CALCULATOR_TYPE.ROR,
            then: Yup.number().required(
                t('form_validation.field_required', { field: t('close_price') }),
            ),
            otherwise: Yup.number(),
        }),
        closeQuantity: Yup.number().when('calculatorType', {
            is: CALCULATOR_TYPE.ROR,
            then: Yup.number()
                .max(Yup.ref('entryQuantity'), t('form_validation.larger.quantity'))
                .required(t('form_validation.field_required', { field: t('close_quantity') })),
            otherwise: Yup.number(),
        }),
        marginAdded: Yup.number().when('calculatorType', {
            is: CALCULATOR_TYPE.LIQ,
            then: Yup.number().required(
                t('form_validation.field_required', { field: t('margin_added') }),
            ),
            otherwise: Yup.number(),
        }),
    });
};

const CalculatorModal: FC<CalculatorModalProps> = ({ isOpen, handleClose }) => {
    const { colors } = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { quoteTokenName, symbolId } = useAppSelector((state) => state.derivatives.currentSymbol);
    const {
        configV2: { futuresSymbol, token },
    } = useAppSelector((state) => state.global);
    const {
        setFieldValue,
        values,
        handleSubmit,
        errors,
        setErrors,
        setFieldError,
        isValid,
        isSubmitting,
    } = useFormik({
        initialValues: initialCalculatorValues,
        validationSchema: calculatorValidationSchema(t),
        validateOnChange: false,
        onSubmit: async (values) => {
            const queryParams: CalculatorQueryParamsType = {
                symbol_id: values.symbol,
                is_long: values.side,
                leverage: values.leverage,
                open_price: values.openPrice,
                open_quantity: values.entryQuantity,
                close_price: values.closePrice,
                close_quantity: values.closeQuantity,
                margin_call: values.marginAdded,
                calculatorType: values.calculatorType,
            };
            const result = await dispatch(performCalculations(queryParams)).unwrap();
            if (result) {
                if (values.calculatorType === CALCULATOR_TYPE.ROR) {
                    setRorCalculationResult(result);
                    setElpCalculationResult({} as ELPCalculationType);
                } else {
                    setElpCalculationResult(result);
                    setRorCalculationResult({} as RORCalculationType);
                }
            }
        },
    });
    const [elpCalculationResult, setElpCalculationResult] = useState({} as ELPCalculationType);
    const [rorCalculationResult, setRorCalculationResult] = useState({} as RORCalculationType);

    const handleChange = (value: string | number, name: string | undefined) => {
        if (name === 'calculatorType' && value !== values.calculatorType) {
            setErrors({});
        }
        //don't change when change is not according to Regex
        if (
            [
                'openPrice',
                'closePrice',
                'entryQuantity',
                'closeQuantity',
                'leverage',
                'marginAdded',
            ].includes(name!) &&
            !REGEX.DECIMAL_NUMBERS.test(value.toString())
        ) {
            return;
        }
        setFieldValue(name!, value);
        setFieldError(name!, undefined);
    };

    useEffect(() => {
        setFieldValue('symbol', symbolId);
    }, [symbolId]);

    return (
        <StyledModal size="lg" open={isOpen}>
            <form onSubmit={handleSubmit}>
                <ModalTitle>
                    <Tabs
                        className="calculator-type"
                        name="calculatorType"
                        value={values.calculatorType}
                        onChange={handleChange}
                    >
                        {Object.keys(CALCULATOR_TYPE).map((tab, index) => {
                            return (
                                <Tab
                                    value={CALCULATOR_TYPE[tab as keyof typeof CALCULATOR_TYPE]}
                                    key={index}
                                    label={t(CALCULATOR_TYPE[tab as keyof typeof CALCULATOR_TYPE])}
                                />
                            );
                        })}
                    </Tabs>
                    <Cross
                        fill={colors.text.primary}
                        onClick={handleClose}
                        className="clickable"
                        style={{ height: 20, width: 20 }}
                    />
                </ModalTitle>
                <ModalContent>
                    <div className="col col-left">
                        <Select
                            name="symbol"
                            value={values.symbol}
                            onChange={handleChange}
                            fullWidth={true}
                        >
                            {Object.keys(futuresSymbol).map((key) => (
                                <SelectItem value={futuresSymbol[key].symbolId} key={key}>
                                    <img
                                        src={
                                            token[
                                                futuresSymbol[key]
                                                    .secondLevelUnderlyingId as keyof TokenObj
                                            ]?.iconUrl || defaultImage
                                        }
                                        alt={futuresSymbol[key].symbolId}
                                    />
                                    {futuresSymbol[key].symbolName}
                                </SelectItem>
                            ))}
                        </Select>
                        <Tabs
                            className="calculation-side"
                            name="side"
                            value={values.side}
                            onChange={handleChange}
                        >
                            {Object.keys(POSITION_SIDE).map((tab, index) => {
                                return (
                                    <Tab
                                        value={POSITION_SIDE[tab as keyof typeof POSITION_SIDE]}
                                        key={index}
                                        label={t(tab.toLowerCase())}
                                        isButton={true}
                                    />
                                );
                            })}
                        </Tabs>
                        <Input
                            hasTooltip={false}
                            name="leverage"
                            onChange={handleChange}
                            value={values.leverage}
                            placeholder={t('leverage')}
                            fullWidth={true}
                            error={errors.leverage}
                        />
                        <Input
                            hasTooltip={false}
                            name="openPrice"
                            onChange={handleChange}
                            value={values.openPrice}
                            placeholder={t('open_price')}
                            fullWidth={true}
                            error={errors.openPrice}
                            rightElement={quoteTokenName}
                        />
                        <Input
                            hasTooltip={false}
                            name="entryQuantity"
                            onChange={handleChange}
                            value={values.entryQuantity}
                            fullWidth={true}
                            placeholder={t('entry_quantity')}
                            error={errors.entryQuantity}
                            rightElement={t('qty')}
                        />
                        {values.calculatorType === CALCULATOR_TYPE.ROR ? (
                            <>
                                <Input
                                    hasTooltip={false}
                                    name="closePrice"
                                    onChange={handleChange}
                                    value={values.closePrice}
                                    fullWidth={true}
                                    placeholder={t('close_price')}
                                    error={errors.closePrice}
                                    rightElement={quoteTokenName}
                                />
                                <Input
                                    hasTooltip={false}
                                    name="closeQuantity"
                                    onChange={handleChange}
                                    value={values.closeQuantity}
                                    fullWidth={true}
                                    placeholder={t('close_quantity')}
                                    error={errors.closeQuantity}
                                    rightElement={t('qty')}
                                />
                            </>
                        ) : (
                            <Input
                                hasTooltip={false}
                                name="marginAdded"
                                onChange={handleChange}
                                value={values.marginAdded}
                                fullWidth={true}
                                placeholder={t('margin_added')}
                                error={errors.marginAdded}
                                rightElement={quoteTokenName}
                            />
                        )}
                        <Button
                            fullWidth={true}
                            disabled={!isValid || isSubmitting}
                            loading={isSubmitting}
                        >
                            {t('calculate')}
                        </Button>
                    </div>
                    <div className="col col-right">
                        {values.calculatorType === CALCULATOR_TYPE.ROR ? (
                            <>
                                <div className="calculations">
                                    <div className="title">{t('open_margin')}:</div>
                                    <div className="value">
                                        {rorCalculationResult.orderMargin
                                            ? `${rorCalculationResult.orderMargin} ${quoteTokenName}`
                                            : '--'}
                                    </div>
                                </div>
                                <div className="calculations">
                                    <div className="title">{t('taker_fee_open')}:</div>
                                    <div className="value">
                                        {rorCalculationResult.takerOpenFee
                                            ? `${rorCalculationResult.takerOpenFee} ${quoteTokenName}`
                                            : '--'}
                                    </div>
                                </div>
                                <div className="calculations">
                                    <div className="title">{t('maker_fee_open')}:</div>
                                    <div className="value">
                                        {rorCalculationResult.makerOpenFee
                                            ? `${rorCalculationResult.makerOpenFee} ${quoteTokenName}`
                                            : '--'}
                                    </div>
                                </div>
                                <div className="calculations">
                                    <div className="title">{t('taker_fee_close')}:</div>
                                    <div className="value">
                                        {rorCalculationResult.takerCloseFee
                                            ? `${rorCalculationResult.takerCloseFee} ${quoteTokenName}`
                                            : '--'}
                                    </div>
                                </div>
                                <div className="calculations">
                                    <div className="title">{t('maker_fee_close')}:</div>
                                    <div className="value">
                                        {rorCalculationResult.makerCloseFee
                                            ? `${rorCalculationResult.makerCloseFee} ${quoteTokenName}`
                                            : '--'}
                                    </div>
                                </div>
                                <div className="calculations">
                                    <div className="title">{t('return')}:</div>
                                    <div className="value">
                                        {rorCalculationResult.profit
                                            ? `${rorCalculationResult.profit} ${quoteTokenName}`
                                            : '--'}
                                    </div>
                                </div>
                                <div className="calculations">
                                    <div className="title">{t('rate_of_return_%')}:</div>
                                    <div className="value">
                                        {rorCalculationResult.profitRate
                                            ? Math.floor(
                                                  Number(rorCalculationResult.profitRate) * 10000,
                                              ) / 100
                                            : '--'}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="calculations">
                                <div className="title">
                                    {`${t('liquidation_price')} (${quoteTokenName}):`}
                                </div>
                                <div className="value">
                                    {elpCalculationResult.liquidationPrice
                                        ? `${elpCalculationResult.liquidationPrice} ${quoteTokenName}`
                                        : '--'}
                                </div>
                            </div>
                        )}
                    </div>
                </ModalContent>
            </form>
        </StyledModal>
    );
};

export { CalculatorModal };
